import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Plus, Search, Mail, Phone, MapPin, Edit, Trash2, Filter, ArrowUpDown, ArrowUp, ArrowDown, LayoutGrid, List, Table as TableIcon, X, Badge as BadgeIcon, Eye, Merge, GitMerge, UserX } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatInCST } from '../utils/timezone';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import CustomerDetailDialog from '../components/CustomerDetailDialog';

export default function Customers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState(() => window.innerWidth < 768 ? 'list' : 'table');
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('company_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [filters, setFilters] = useState({
    status: 'active',
    show_merged: false
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeTarget, setMergeTarget] = useState('');
  const [merging, setMerging] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    email: '',
    phone: '',
    contact_name: '',
    status: 'active',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    notes: '',
    domain: ''
  });

  useEffect(() => {
    loadCustomers();
  }, [searchTerm, filters, sortBy, sortOrder, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm, filters, sortBy, sortOrder]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const params = {
        skip: currentPage * pageSize,
        limit: pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      if (searchTerm) params.search = searchTerm;
      if (filters.status) params.status = filters.status;
      if (filters.show_merged) params.show_merged = true;
      
      const response = await api.get('/customers', { params });
      const data = response.data;
      if (data && data.customers) {
        setCustomers(data.customers);
        setTotalCustomers(data.total || 0);
      } else if (Array.isArray(data)) {
        setCustomers(data);
        setTotalCustomers(data.length);
      }
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Remove empty strings for optional email fields to avoid validation errors
      const dataToSubmit = { ...formData };
      if (!dataToSubmit.email) delete dataToSubmit.email;
      if (!dataToSubmit.phone) delete dataToSubmit.phone;
      if (!dataToSubmit.contact_name) delete dataToSubmit.contact_name;
      if (!dataToSubmit.address) delete dataToSubmit.address;
      if (!dataToSubmit.city) delete dataToSubmit.city;
      if (!dataToSubmit.state) delete dataToSubmit.state;
      if (!dataToSubmit.zip_code) delete dataToSubmit.zip_code;
      if (!dataToSubmit.notes) delete dataToSubmit.notes;
      
      if (editingCustomer) {
        await api.put(`/customers/${editingCustomer.id}`, dataToSubmit);
        toast.success('Customer updated successfully');
      } else {
        await api.post('/customers', dataToSubmit);
        toast.success('Customer created successfully');
      }
      setDialogOpen(false);
      resetForm();
      loadCustomers();
    } catch (error) {
      const errorMessage = error.response?.data?.detail;
      if (Array.isArray(errorMessage)) {
        toast.error(errorMessage.map(e => e.msg).join(', '));
      } else if (typeof errorMessage === 'string') {
        toast.error(errorMessage);
      } else {
        toast.error('An error occurred');
      }
      console.error('Customer operation error:', error.response?.data);
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      company_name: customer.company_name,
      email: customer.email || '',
      phone: customer.phone || '',
      contact_name: customer.contact_name || '',
      status: customer.status || 'active',
      address: customer.address || '',
      city: customer.city || '',
      state: customer.state || '',
      zip_code: customer.zip_code || '',
      notes: customer.notes || '',
      domain: customer.domain || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    
    try {
      await api.delete(`/customers/${customerId}`);
      toast.success('Customer deleted successfully');
      loadCustomers();
    } catch (error) {
      toast.error('Failed to delete customer');
    }
  };

  const toggleCustomerSelection = (customerId) => {
    setSelectedCustomers(prev =>
      prev.includes(customerId)
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedCustomers.length === customers.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(customers.map(c => c.id));
    }
  };

  const handleMergeCustomers = async () => {
    if (selectedCustomers.length < 2 || !mergeTarget) return;
    setMerging(true);
    try {
      const sourceIds = selectedCustomers.filter(id => id !== mergeTarget);
      const response = await api.post('/customers/merge', {
        source_customer_ids: sourceIds,
        target_customer_id: mergeTarget,
      });
      const r = response.data;
      toast.success(
        `Merged ${r.merged_customers.length} customer(s) into ${r.target_customer}. ` +
        `Moved: ${r.tickets_moved} tickets, ${r.invoices_moved} invoices, ${r.assets_moved} assets`
      );
      setMergeDialogOpen(false);
      setSelectedCustomers([]);
      setMergeTarget('');
      loadCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to merge customers');
    } finally {
      setMerging(false);
    }
  };

  const openBulkDeleteDialog = () => {
    setDeleteStep(1);
    setDeleteConfirmText('');
    setBulkDeleteDialogOpen(true);
  };

  const handleBulkDelete = async () => {
    setDeleting(true);
    try {
      const response = await api.post('/customers/bulk-delete', {
        ids: selectedCustomers,
        confirmation_text: 'DELETE',
        confirmed_count: selectedCustomers.length,
      });
      const r = response.data;
      toast.success(`Deleted ${r.deleted} customer(s)`);
      if (r.orphaned_tickets > 0 || r.orphaned_invoices > 0 || r.orphaned_assets > 0) {
        toast.info(`Note: ${r.orphaned_tickets} tickets, ${r.orphaned_invoices} invoices, and ${r.orphaned_assets} assets are now unlinked`);
      }
      setBulkDeleteDialogOpen(false);
      setSelectedCustomers([]);
      loadCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete customers');
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkSetInactive = async () => {
    try {
      const res = await api.post('/customers/bulk-status', { ids: selectedCustomers, status: 'inactive' });
      toast.success(`${res.data.updated} customer(s) set to inactive`);
      setSelectedCustomers([]);
      loadCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const resetForm = () => {
    setFormData({
      company_name: '',
      email: '',
      phone: '',
      contact_name: '',
      status: 'active',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      notes: ''
    });
    setEditingCustomer(null);
  };

  const handleDialogChange = (open) => {
    setDialogOpen(open);
    if (!open) resetForm();
  };

  const clearFilters = () => {
    setFilters({ status: 'active', show_merged: false });
  };

  const hasActiveFilters = (filters.status && filters.status !== 'active') || filters.show_merged;

  const handleColumnSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) {
      return <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" />;
    }
    return sortOrder === 'asc' ? 
      <ArrowUp className="h-3.5 w-3.5 text-blue-600" /> : 
      <ArrowDown className="h-3.5 w-3.5 text-blue-600" />;
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 transition-colors',
      inactive: 'bg-white/10 text-[#b1c3ce] hover:bg-white/15 transition-colors',
      merged: 'bg-sky-500/15 text-sky-300 hover:bg-sky-500/25 transition-colors',
      pending: 'bg-amber-500/15 text-amber-300 hover:bg-amber-500/25 transition-colors'
    };
    return colors[status] || colors.active;
  };

  const handleViewCustomer = (customer) => {
    navigate(`/customers/${customer.id}`);
  };

  const handleCustomerUpdate = () => {
    loadCustomers();
  };

  return (
    <div className="co-page co-scope space-y-4 sm:space-y-6" data-testid="customers-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Customers</h1>
          <p className="text-muted-foreground text-sm sm:text-base mt-1">Manage your customer accounts</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={handleDialogChange}>
          <DialogTrigger asChild>
            <Button data-testid="add-customer-button">
              <Plus className="h-4 w-4 mr-2" />
              Add Customer
            </Button>
          </DialogTrigger>
          <DialogContent className="co-scope max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="company_name">Company Name *</Label>
                  <Input
                    id="company_name"
                    data-testid="company-name-input"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="contact_name">Contact Name</Label>
                  <Input
                    id="contact_name"
                    data-testid="contact-name-input"
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    data-testid="email-input"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="domain">Customer Domain</Label>
                  <Input
                    id="domain"
                    value={formData.domain}
                    onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                    placeholder="acmecorp.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Primary domain for this customer (e.g., acmecorp.com). Used for automatic email routing.
                  </p>
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    data-testid="phone-input"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="zip_code">ZIP Code</Label>
                  <Input
                    id="zip_code"
                    value={formData.zip_code}
                    onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => handleDialogChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-customer-button">
                  {editingCustomer ? 'Update' : 'Create'} Customer
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Controls Bar: Search, Sort, Filters, View Mode */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 w-full sm:max-w-sm sm:flex-1">
          <Search className="h-5 w-5 text-gray-400 hidden sm:block" />
          <Input
            placeholder="Search customers..."
            data-testid="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border-neutral-300 focus:border-brand-primary"
          />
        </div>

        <div className="flex items-center gap-2 flex-wrap">
        {/* Sort Controls */}
        <div className="flex items-center gap-1 sm:gap-2">
          <Label className="text-xs sm:text-sm font-medium text-gray-700 hidden sm:block">Sort:</Label>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[130px] sm:w-[160px] h-9" data-testid="sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="company_name">Company Name</SelectItem>
              <SelectItem value="created_at">Created Date</SelectItem>
              <SelectItem value="email">Email</SelectItem>
              <SelectItem value="status">Status</SelectItem>
            </SelectContent>
          </Select>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            data-testid="sort-order-toggle"
            className="h-9 px-2"
          >
            <ArrowUpDown className="h-4 w-4" />
          </Button>
        </div>

        {/* Filter Toggle */}
        <Button
          variant={showFilters ? 'default' : 'outline'}
          onClick={() => setShowFilters(!showFilters)}
          data-testid="filter-toggle"
          className="h-9"
        >
          <Filter className="h-4 w-4 mr-2" />
          Filters
          {hasActiveFilters && (
            <Badge className="ml-2 bg-blue-600 text-white text-xs">
              {Object.values(filters).filter(f => f !== '').length}
            </Badge>
          )}
        </Button>

        {/* View Mode Switcher */}
        <div className="flex gap-1 border rounded-md p-1 bg-white">
          <Button
            size="sm"
            variant={viewMode === 'card' ? 'default' : 'ghost'}
            onClick={() => setViewMode('card')}
            data-testid="card-view-button"
            className="h-8 px-3"
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant={viewMode === 'list' ? 'default' : 'ghost'}
            onClick={() => setViewMode('list')}
            data-testid="list-view-button"
            className="h-8 px-3"
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant={viewMode === 'table' ? 'default' : 'ghost'}
            onClick={() => setViewMode('table')}
            data-testid="table-view-button"
            className="h-8 px-3"
          >
            <TableIcon className="h-4 w-4" />
          </Button>
        </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card className="bg-white/[0.03] border-[#193b4b]">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs font-semibold mb-1 block">Status</Label>
                <Select 
                  value={filters.status} 
                  onValueChange={(value) => setFilters({...filters, status: value || ''})}
                >
                  <SelectTrigger className="h-9 bg-white" data-testid="filter-status">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="merged">Merged</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-end gap-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={filters.show_merged}
                    onCheckedChange={(checked) => setFilters({...filters, show_merged: checked})}
                    id="show-merged-customers"
                    data-testid="show-merged-customers"
                  />
                  <Label htmlFor="show-merged-customers" className="text-sm cursor-pointer">
                    Show merged customers
                  </Label>
                </div>
                {hasActiveFilters && (
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={clearFilters} 
                    className="h-9"
                    data-testid="clear-filters-button"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Clear Filters
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Bar */}
      {selectedCustomers.length >= 1 && (
        <div data-testid="action-bar" className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <span className="text-sm font-medium text-blue-800">
            {selectedCustomers.length} customer(s) selected
          </span>
          <div className="flex items-center gap-2">
            {selectedCustomers.length >= 2 && (
              <Button
                size="sm"
                onClick={() => { setMergeTarget(''); setMergeDialogOpen(true); }}
                className="bg-[#1a5490] hover:bg-[#0f3a5f]"
                data-testid="merge-customers-btn"
              >
                <GitMerge className="h-4 w-4 mr-2" />
                Merge Customers
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              className="text-orange-600 hover:bg-orange-50 border-orange-200"
              onClick={handleBulkSetInactive}
              data-testid="bulk-inactive-customers-btn"
            >
              <UserX className="h-4 w-4 mr-2" />
              Make Inactive {selectedCustomers.length > 1 ? `(${selectedCustomers.length})` : ''}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="text-red-600 hover:bg-red-50 border-red-200"
              onClick={openBulkDeleteDialog}
              data-testid="bulk-delete-customers-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete {selectedCustomers.length > 1 ? `(${selectedCustomers.length})` : ''}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelectedCustomers([])}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <Card>
        <CardContent className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <img 
                src="/cipherone-logo.png" 
                alt="Loading" 
                className="h-12 w-12 logo-spin mx-auto mb-2"
              />
              <div>Loading customers...</div>
            </div>
          ) : customers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No customers found. {hasActiveFilters ? 'Try adjusting your filters.' : 'Create your first customer!'}
            </div>
          ) : viewMode === 'card' ? (
            /* Card View */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {customers.map((customer) => (
                <Card 
                  key={customer.id} 
                  className="hover:shadow-md transition-shadow cursor-pointer" 
                  data-testid="customer-card"
                  onClick={() => handleViewCustomer(customer)}
                >
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{customer.company_name}</h3>
                        <span className={`inline-block px-2 py-1 text-xs rounded-full mt-1 ${
                          customer.status === 'active' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {customer.status}
                        </span>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewCustomer(customer);
                          }}
                          data-testid="view-customer-button"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(customer);
                          }}
                          data-testid="edit-customer-button"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(customer.id);
                          }}
                          data-testid="delete-customer-button"
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                    {customer.contact_name && (
                      <p className="text-sm text-gray-600 mb-2">{customer.contact_name}</p>
                    )}
                    <div className="space-y-1 text-sm">
                      {customer.email && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Mail className="h-3 w-3" />
                          <span className="truncate">{customer.email}</span>
                        </div>
                      )}
                      {customer.phone && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone className="h-3 w-3" />
                          <span>{customer.phone}</span>
                        </div>
                      )}
                      {customer.city && customer.state && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <MapPin className="h-3 w-3" />
                          <span>{customer.city}, {customer.state}</span>
                        </div>
                      )}
                    </div>
                    <div className="mt-3 pt-3 border-t text-xs text-gray-500">
                      Created {formatInCST(customer.created_at, 'MMM d, yyyy')}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : viewMode === 'list' ? (
            /* List View */
            <div className="space-y-2">
              {customers.map((customer) => (
                <Card 
                  key={customer.id} 
                  className="hover:shadow-sm transition-shadow border-l-4 cursor-pointer"
                  style={{
                    borderLeftColor: customer.status === 'active' ? '#22c55e' : '#6b7280'
                  }}
                  onClick={() => handleViewCustomer(customer)}
                  data-testid="customer-list-row"
                >
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3">
                          <h3 className="font-semibold text-base">{customer.company_name}</h3>
                          <Badge className={getStatusColor(customer.status)}>
                            {customer.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600 mt-1 flex-wrap">
                          {customer.contact_name && <span>{customer.contact_name}</span>}
                          {customer.email && (
                            <>
                              {customer.contact_name && <span>•</span>}
                              <span className="flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {customer.email}
                              </span>
                            </>
                          )}
                          {customer.phone && (
                            <>
                              <span>•</span>
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {customer.phone}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewCustomer(customer);
                          }}
                          data-testid="view-customer-button"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(customer);
                          }}
                          data-testid="edit-customer-button"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(customer.id);
                          }}
                          data-testid="delete-customer-button"
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            /* Table View */
            <div className="overflow-x-auto -mx-3 sm:mx-0 lg:overflow-visible">
            <Table>
              <TableHeader>
                <TableRow className="bg-white/5">
                  <TableHead className="w-[40px]">
                    <Checkbox
                      checked={selectedCustomers.length === customers.length && customers.length > 0}
                      onCheckedChange={toggleSelectAll}
                      data-testid="select-all-customers"
                    />
                  </TableHead>
                  <TableHead 
                    className="font-semibold cursor-pointer hover:bg-neutral-100 transition-colors select-none"
                    onClick={() => handleColumnSort('company_name')}
                    data-testid="sort-header-company"
                  >
                    <div className="flex items-center gap-2">
                      Company Name
                      {getSortIcon('company_name')}
                    </div>
                  </TableHead>
                  <TableHead className="font-semibold hidden lg:table-cell">Contact</TableHead>
                  <TableHead 
                    className="font-semibold cursor-pointer hover:bg-neutral-100 transition-colors select-none hidden md:table-cell"
                    onClick={() => handleColumnSort('email')}
                    data-testid="sort-header-email"
                  >
                    <div className="flex items-center gap-2">
                      Email
                      {getSortIcon('email')}
                    </div>
                  </TableHead>
                  <TableHead className="font-semibold hidden lg:table-cell">Phone</TableHead>
                  <TableHead className="font-semibold hidden xl:table-cell">Location</TableHead>
                  <TableHead 
                    className="font-semibold cursor-pointer hover:bg-neutral-100 transition-colors select-none"
                    onClick={() => handleColumnSort('status')}
                    data-testid="sort-header-status"
                  >
                    <div className="flex items-center gap-2">
                      Status
                      {getSortIcon('status')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="font-semibold cursor-pointer hover:bg-neutral-100 transition-colors select-none hidden md:table-cell"
                    onClick={() => handleColumnSort('created_at')}
                    data-testid="sort-header-created"
                  >
                    <div className="flex items-center gap-2">
                      Created
                      {getSortIcon('created_at')}
                    </div>
                  </TableHead>
                  <TableHead className="font-semibold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customers.map((customer) => (
                  <TableRow 
                    key={customer.id} 
                    className="hover:bg-neutral-50 border-b border-neutral-100 cursor-pointer"
                    onClick={() => handleViewCustomer(customer)}
                    data-testid="customer-table-row"
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedCustomers.includes(customer.id)}
                        onCheckedChange={() => toggleCustomerSelection(customer.id)}
                        data-testid={`select-customer-${customer.id}`}
                      />
                    </TableCell>
                    <TableCell className="font-semibold">
                      <div>{customer.company_name}</div>
                      {customer.merged_into && (
                        <Badge variant="outline" className="ml-0 mt-0.5 text-xs text-neutral-500">Merged</Badge>
                      )}
                      <div className="md:hidden text-xs text-muted-foreground font-normal mt-0.5">
                        {customer.contact_name && <span>{customer.contact_name}</span>}
                        {customer.email && <span className="block truncate max-w-[180px]">{customer.email}</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm hidden lg:table-cell">{customer.contact_name || '-'}</TableCell>
                    <TableCell className="text-sm font-mono text-xs hidden md:table-cell">{customer.email || '-'}</TableCell>
                    <TableCell className="text-sm hidden lg:table-cell">{customer.phone || '-'}</TableCell>
                    <TableCell className="text-sm hidden xl:table-cell">
                      {customer.city && customer.state ? `${customer.city}, ${customer.state}` : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(customer.status)}>
                        {customer.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-gray-500 hidden md:table-cell">
                      {formatInCST(customer.created_at, 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewCustomer(customer);
                          }}
                          data-testid="view-customer-button"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(customer);
                          }}
                          data-testid="edit-customer-button"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(customer.id);
                          }}
                          data-testid="delete-customer-button"
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          )}

          {/* Pagination */}
          {!loading && customers.length > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-4 border-t mt-4" data-testid="customers-pagination">
              <div className="text-xs sm:text-sm text-neutral-500">
                Showing {currentPage * pageSize + 1}–{Math.min((currentPage + 1) * pageSize, totalCustomers)} of {totalCustomers}
              </div>
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-xs sm:text-sm text-neutral-500">Per page:</span>
                  <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(v === 'all' ? 10000 : parseInt(v)); setCurrentPage(0); }}>
                    <SelectTrigger className="w-[80px] h-8" data-testid="customer-page-size"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="10000">All</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" disabled={currentPage === 0} onClick={() => setCurrentPage(0)} className="h-8 px-2 hidden sm:inline-flex">First</Button>
                  <Button variant="outline" size="sm" disabled={currentPage === 0} onClick={() => setCurrentPage(p => Math.max(0, p - 1))} className="h-8 w-8 p-0">
                    <ArrowUp className="h-4 w-4 rotate-[-90deg]" />
                  </Button>
                  <span className="text-xs sm:text-sm text-neutral-600 px-1 sm:px-2 min-w-[60px] sm:min-w-[80px] text-center">
                    {currentPage + 1} / {Math.max(1, Math.ceil(totalCustomers / pageSize))}
                  </span>
                  <Button variant="outline" size="sm" disabled={currentPage >= Math.ceil(totalCustomers / pageSize) - 1} onClick={() => setCurrentPage(p => p + 1)} className="h-8 w-8 p-0">
                    <ArrowDown className="h-4 w-4 rotate-[-90deg]" />
                  </Button>
                  <Button variant="outline" size="sm" disabled={currentPage >= Math.ceil(totalCustomers / pageSize) - 1} onClick={() => setCurrentPage(Math.ceil(totalCustomers / pageSize) - 1)} className="h-8 px-2 hidden sm:inline-flex">Last</Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <CustomerDetailDialog
        customer={selectedCustomer}
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
        onUpdate={handleCustomerUpdate}
      />

      {/* Merge Customers Dialog */}
      <Dialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
        <DialogContent className="co-scope max-w-lg" data-testid="merge-customers-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitMerge className="h-5 w-5 text-blue-600" />
              Merge Customers
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-neutral-600">
              Select the <strong>primary customer</strong> to keep. All tickets, invoices, and assets
              from the other selected customers will be moved to the primary customer.
              The merged customers will be marked as inactive.
            </p>

            <div className="space-y-2">
              <Label className="text-sm font-semibold">Primary Customer (keep)</Label>
              <RadioGroup value={mergeTarget} onValueChange={setMergeTarget}>
                {selectedCustomers.map(id => {
                  const c = customers.find(cust => cust.id === id);
                  if (!c) return null;
                  return (
                    <div key={id} className="flex items-center gap-3 p-3 rounded-lg border hover:bg-neutral-50 cursor-pointer" onClick={() => setMergeTarget(id)}>
                      <RadioGroupItem value={id} id={`merge-${id}`} data-testid={`merge-target-${id}`} />
                      <Label htmlFor={`merge-${id}`} className="cursor-pointer flex-1">
                        <span className="font-medium">{c.company_name}</span>
                        {c.email && <span className="text-xs text-neutral-500 ml-2">{c.email}</span>}
                        {c.contact_name && <span className="text-xs text-neutral-400 ml-2">({c.contact_name})</span>}
                      </Label>
                      {mergeTarget === id && (
                        <Badge className="bg-blue-100 text-blue-700">Primary</Badge>
                      )}
                    </div>
                  );
                })}
              </RadioGroup>
            </div>

            {mergeTarget && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                <strong>Summary:</strong> {selectedCustomers.length - 1} customer(s) will be merged into{' '}
                <strong>{customers.find(c => c.id === mergeTarget)?.company_name}</strong>.
                <ul className="mt-1 ml-4 list-disc text-xs">
                  <li>All tickets will be reassigned to the primary customer</li>
                  <li>All invoices will be reassigned to the primary customer</li>
                  <li>All assets will be reassigned to the primary customer</li>
                  <li>Merged customers will be set to inactive</li>
                </ul>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setMergeDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleMergeCustomers}
                disabled={!mergeTarget || merging}
                className="bg-[#1a5490] hover:bg-[#0f3a5f]"
                data-testid="confirm-merge-btn"
              >
                {merging ? 'Merging...' : `Merge ${selectedCustomers.length} Customers`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Dialog - Three Step Verification */}
      <Dialog open={bulkDeleteDialogOpen} onOpenChange={(open) => { setBulkDeleteDialogOpen(open); if (!open) { setDeleteStep(1); setDeleteConfirmText(''); } }}>
        <DialogContent className="co-scope max-w-md" data-testid="bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete {selectedCustomers.length} Customer(s)
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* Step Indicators */}
            <div className="flex items-center gap-2">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center gap-1">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${deleteStep >= s ? 'bg-red-600 text-white' : 'bg-neutral-200 text-neutral-500'}`}>
                    {s}
                  </div>
                  {s < 3 && <div className={`w-8 h-0.5 ${deleteStep > s ? 'bg-red-600' : 'bg-neutral-200'}`} />}
                </div>
              ))}
              <span className="text-xs text-neutral-500 ml-2">Step {deleteStep} of 3</span>
            </div>

            {/* Step 1: Warning */}
            {deleteStep === 1 && (
              <div className="space-y-3">
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                  <p className="font-semibold mb-2">Warning: This action is permanent and cannot be undone.</p>
                  <p>You are about to delete <strong>{selectedCustomers.length}</strong> customer(s):</p>
                  <ul className="mt-2 ml-4 list-disc text-xs space-y-1 max-h-[120px] overflow-y-auto">
                    {selectedCustomers.map(id => {
                      const c = customers.find(cust => cust.id === id);
                      return <li key={id}>{c?.company_name || 'Unknown'}</li>;
                    })}
                  </ul>
                </div>
                <p className="text-sm text-neutral-600">
                  Associated tickets, invoices, and assets will become unlinked but will <strong>not</strong> be deleted.
                </p>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setBulkDeleteDialogOpen(false)}>Cancel</Button>
                  <Button variant="destructive" onClick={() => setDeleteStep(2)} data-testid="delete-step1-next">
                    I Understand, Continue
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Type DELETE */}
            {deleteStep === 2 && (
              <div className="space-y-3">
                <p className="text-sm text-neutral-700">
                  To confirm deletion of <strong>{selectedCustomers.length}</strong> customer(s), type <strong className="text-red-600 font-mono">DELETE</strong> in the field below:
                </p>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Type DELETE to confirm"
                  className="font-mono text-center text-lg"
                  data-testid="delete-confirm-input"
                  autoFocus
                />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setDeleteStep(1)}>Back</Button>
                  <Button
                    variant="destructive"
                    disabled={deleteConfirmText !== 'DELETE'}
                    onClick={() => setDeleteStep(3)}
                    data-testid="delete-step2-next"
                  >
                    Continue
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Final Confirmation */}
            {deleteStep === 3 && (
              <div className="space-y-3">
                <div className="p-4 bg-red-100 border border-red-300 rounded-lg text-center">
                  <Trash2 className="h-8 w-8 text-red-600 mx-auto mb-2" />
                  <p className="text-lg font-bold text-red-800">Final Confirmation</p>
                  <p className="text-sm text-red-700 mt-1">
                    Permanently delete <strong>{selectedCustomers.length}</strong> customer(s)?
                  </p>
                  <p className="text-xs text-red-600 mt-2">This cannot be reversed.</p>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setDeleteStep(2)}>Back</Button>
                  <Button
                    variant="destructive"
                    onClick={handleBulkDelete}
                    disabled={deleting}
                    data-testid="delete-final-confirm"
                  >
                    {deleting ? 'Deleting...' : `Yes, Delete ${selectedCustomers.length} Customer(s)`}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
